import { Model } from '@mode-switch/model';
import { ChartTypes, DataViewTypes, FlamingoTypes, SwitchView } from '@mode/shared/contract-common';
import {
  generateFormatFromFlatTableFormat,
  getDefaultFieldFormatMapFromColumns,
  getDefaultGeneralFormat,
  getDefaultTableColumnSettingsFromColumns,
} from '@mode/shared/util-js';
import { Switch } from '@mode/shared/util-switch';
import { createFeatureSelector } from '@ngrx/store';
import { SwitchAttr, SwitchAttrs } from 'libs/shared/contract-common/src/lib/switch/switch-attrs.types';
import { createSelector } from 'reselect';
import {
  getDatasetResultByQueryToken,
  getDatasetResultByToken,
  getDatasetResultEntities,
  getDatasetResults,
} from '../dataset-result/dataset-result.selectors';
import { getSwitchAttrsFeatureByToken, getSwitchAttrsFeatureEntities } from '../switch-attrs/switch-attrs.selectors';
import { makeColumnViewFromResults, makeViewFromFeatureAndResults } from '../switch-resource-helpers';
import * as fromSwitchView from './switch-view.reducer';

const DEFAULT_IMPORTED_SWITCH_VIEW_TITLE = 'Imported Dataset';

export const selectSwitchViewState = createFeatureSelector<fromSwitchView.SwitchViewFeatureState>(
  fromSwitchView.switchViewFeatureKey
);

export const selectViews = createSelector(selectSwitchViewState, (feature) => feature.views);

export const switchViewsLoaded = createSelector(selectSwitchViewState, (state) => state.viewsLoaded);

export const getSwitchViewTokens = createSelector(selectViews, fromSwitchView.selectViewIds);

export const getSwitchViewFeatures = createSelector(selectViews, fromSwitchView.selectAllViews);

export const getSwitchViewFeatureEntities = createSelector(selectViews, fromSwitchView.selectViewEntities);

export const getSwitchViewFeatureByToken = (token: string) =>
  createSelector(getSwitchViewFeatureEntities, (entities) => entities[token]);

export const getImportedSwitchViewFeatures = createSelector(getSwitchViewFeatures, (switchViewFeatures) => {
  return switchViewFeatures.filter(
    (switchViewFeature) => switchViewFeature.sourceType === DataViewTypes.SwitchViewSourceType.SwitchView
  );
});

export const getSwitchViewsExist = createSelector(
  getSwitchViewTokens,
  (switchViewTokens) => switchViewTokens.length > 0
);

export const getLocalSwitchViewFeatures = createSelector(getSwitchViewFeatures, (switchViewFeatures) => {
  return switchViewFeatures.filter(
    (switchViewFeature) => switchViewFeature.sourceType !== DataViewTypes.SwitchViewSourceType.SwitchView
  );
});

export const getLocalSwitchViewTokens = createSelector(getLocalSwitchViewFeatures, (switchViewFeatures) => {
  return switchViewFeatures.map((switchViewFeature) => switchViewFeature.token);
});

export const getImportedDatasetResults = createSelector(
  getImportedSwitchViewFeatures,
  getDatasetResults,
  (importedSwitchViewFeatures, datasetResults) => {
    const importedSwitchViewTokens = importedSwitchViewFeatures.map((switchView) => switchView.token);
    const importedDatasetResults: DataViewTypes.DatasetResult[] = [];

    importedSwitchViewTokens.map((token) => {
      const datasetResult = datasetResults.find((dr) => dr.switchViewToken === token);
      if (datasetResult) {
        importedDatasetResults.push(datasetResult);
      }
    });

    return importedDatasetResults;
  }
);

export const getEnhancedDatasetResults = createSelector(
  getSwitchViewFeatures,
  getDatasetResults,
  (switchViewFeatures, datasetResults) => {
    const finalDatasetResults: DataViewTypes.EnhancedDatasetResult[] = datasetResults.map((datasetResult) => {
      const switchView = switchViewFeatures.find(
        (switchViewFeature) => switchViewFeature.token === datasetResult.switchViewToken
      );

      const isLocal = switchView?.sourceType !== DataViewTypes.SwitchViewSourceType.SwitchView ?? true;
      const rootSourceModelName = switchView?.rootSourceModelName ?? '';

      const displayName = isLocal
        ? datasetResult.queryName
        : switchView?.rootSourceModelName || DEFAULT_IMPORTED_SWITCH_VIEW_TITLE;

      let state = DataViewTypes.DatasetStates.Default;
      if (switchView?.orphan) {
        state = DataViewTypes.DatasetStates.Orphan;
      } else if (switchView?.isDataRefreshAvailable) {
        state = DataViewTypes.DatasetStates.HasUpdateAvailable;
      } else if (switchView?.isFallbackData) {
        state = DataViewTypes.DatasetStates.LastRunFailed;
      }

      return {
        ...datasetResult,
        isLocal,
        rootSourceModelName,
        rootModelIsSample: switchView?.rootModelIsSample || false,
        displayName,
        state,
        dataRefreshAvailableAsOf: switchView?.dataRefreshAvailableAsOf,
        producerType: switchView?.producerType ?? null,
        rowOrder: switchView?.rowOrder ?? null,
      };
    });

    return finalDatasetResults;
  }
);

export const getSuccessfulEnhancedDatasets = createSelector(getEnhancedDatasetResults, (datasetResults) =>
  datasetResults.filter((datasetResult) => datasetResult.queryRunState === DataViewTypes.QueryRunStatus.Succeeded)
);

export const getSuccessfulQueries = createSelector(getSuccessfulEnhancedDatasets, (datasetResults) =>
  datasetResults.filter((datasetResult) => datasetResult.isLocal)
);

export const getImportedDatasetCount = createSelector(
  getSuccessfulEnhancedDatasets,
  (datasetResults) => datasetResults.filter((datasetResult) => !datasetResult.isLocal).length
);

export const getEnhancedDatasetResultBySwitchViewToken = (switchViewToken: string) =>
  createSelector(getEnhancedDatasetResults, (enhancedDatasetResults) =>
    enhancedDatasetResults.find((enhancedDatasetResult) => enhancedDatasetResult.switchViewToken === switchViewToken)
  );

export const createRootSourceModelNameSelectorBySwitchViewToken = (token: string) =>
  createSelector(getSwitchViewFeatureByToken(token), (view) => {
    if (view?.sourceType === DataViewTypes.SwitchViewSourceType.SwitchView) {
      return view?.rootSourceModelName ? view?.rootSourceModelName : DEFAULT_IMPORTED_SWITCH_VIEW_TITLE;
    }
    return view?.rootSourceModelName;
  });

// TODO: this is a temporary selector to help bridge the gap of a requirement for a model selector in existing
// code paths. once both the report and exploration flow are further along, we can refactor them together
// in whatever way feels appropriate.
// TODO: passing query_token as name here, in line with production behavior, but should be adjusted if we keep a model around
export const getModel = createSelector(
  getSwitchViewFeatureEntities,
  getDatasetResultEntities,
  getSwitchAttrsFeatureEntities,
  (switchViewFeatureEntities, datasetResultEntities, switchAttrsEntities) => {
    // Need to handle it this way to avoid typescript issues
    const switchViewTokens = Object.keys(switchViewFeatureEntities);
    const switchViews: FlamingoTypes.ModeView[] = [];

    switchViewTokens.map((token: string) => {
      const switchViewFeature = switchViewFeatureEntities[token] as SwitchView;
      const datasetResult = datasetResultEntities[token];
      const switchAttrsFeature = switchAttrsEntities[token] as SwitchAttrs;
      switchViewFeature.switchAttrs = switchAttrsFeature.switchAttrsData;
      if (switchViewFeature && datasetResult) {
        switchViews.push(makeViewFromFeatureAndResults(switchViewFeature, datasetResult));
      }
    });

    if (switchViews.length === 1) {
      return Switch.Models.makeFromView(switchViews[0].name, switchViews[0]);
    } else {
      return Switch.Models.make('', switchViews);
    }
  }
);

export const getModelByQueryToken = (token: string) =>
  createSelector(getModel, (model) => model.views.filter((view) => view.name === token));

export const getModelFormatsByQueryToken = (token: string) =>
  createSelector(getModelByQueryToken(token), (view) => {
    const { attrs } = view[0];
    const columns = attrs.map((attr) => ({
      alias: attr.name,
      name: attr.name,
      source: `[${attr.name}]`,
      type: attr.formula.type,
    }));

    const format = generateFormatFromFlatTableFormat({
      general: {
        ...getDefaultGeneralFormat(ChartTypes.FlatTableDefaultGeneralFormat.Results),
        sizingStyle: 'sizeToFit',
      },
      columns: getDefaultTableColumnSettingsFromColumns(columns),
    });

    const fieldsFormat = getDefaultFieldFormatMapFromColumns(columns);

    return {
      format,
      fieldsFormat,
    };
  });

/*
  Needed to filter out datasets before alpha,
  can remove after alpha and when datasets are supported
*/
export const getModelWithoutDatasets = createSelector(
  getSwitchViewFeatureEntities,
  getDatasetResultEntities,
  getEnhancedDatasetResults,
  (switchViewFeatureEntities, datasetResultEntities, enhancedDatasetResults) => {
    const querySwitchViewTokens: string[] = [];
    enhancedDatasetResults.map((datasetResult) => {
      return datasetResult.isLocal === true ? querySwitchViewTokens.push(datasetResult.switchViewToken) : null;
    });
    // Need to handle it this way to avoid typescript issues
    const switchViewTokens = Object.keys(switchViewFeatureEntities);
    const switchViews: FlamingoTypes.ModeView[] = [];

    switchViewTokens.map((token: string) => {
      if (querySwitchViewTokens.includes(token)) {
        const switchViewFeature = switchViewFeatureEntities[token];
        const datasetResult = datasetResultEntities[token];
        if (switchViewFeature && datasetResult) {
          switchViews.push(makeViewFromFeatureAndResults(switchViewFeature, datasetResult));
        }
      }
    });

    if (switchViews.length === 1) {
      return Switch.Models.makeFromView(switchViews[0].name, switchViews[0]);
    } else {
      return Switch.Models.make('', switchViews);
    }
  }
);

export const getSwitchViewByToken = (token: string) =>
  createSelector(
    getSwitchViewFeatureByToken(token),
    getDatasetResultByToken(token),
    getSwitchAttrsFeatureByToken(token),
    (switchViewResource, datasetResult, switchAttrs) => {
      const switchView = switchViewResource as SwitchView;
      if (switchView) {
        switchView.switchAttrs = (switchAttrs as SwitchAttrs).switchAttrsData as unknown as SwitchAttr[];
      }

      // TODO: does this mean this should be an observable in a facade, not a selector?
      // How do multiple places use it then?
      if (switchViewResource && datasetResult) {
        return {
          ...makeViewFromFeatureAndResults(switchView, datasetResult),
          sourceType: switchViewResource?.sourceType,
        };
      }

      return;
    }
  );

export const createColumnsSwitchModelSelectorByQueryToken = (queryToken: string) =>
  createSelector(getDatasetResultByQueryToken(queryToken), (datasetResults) => {
    if (datasetResults) {
      const view = makeColumnViewFromResults(datasetResults);
      return Switch.Models.makeFromView(view.name, view);
    }

    return;
  });

// TODO: This also needs to be updated to be based off of switchViewToken!
// This can be done after the BE finishes the data rearchitecture, but must
// be done before the current GF milestone finished.
export const createRunReadySelectorByQueryToken = (queryToken: string) => {
  return createSelector(
    getDatasetResultByQueryToken(queryToken),
    getModel,
    baseRunReadyProjector({
      queryStatus: [DataViewTypes.QueryRunStatus.Succeeded],
    })
  );
};

export const createRunReadyOrRunningSelectorByQueryToken = (queryToken: string) => {
  return createSelector(
    getDatasetResultByQueryToken(queryToken),
    getModel,
    baseRunReadyProjector({
      // Success and "running" states to allow previous encodings to render/show.
      queryStatus: [
        DataViewTypes.QueryRunStatus.Succeeded,
        DataViewTypes.QueryRunStatus.Pending,
        DataViewTypes.QueryRunStatus.Enqueued,
      ],
    })
  );
};

export const getRunReadyWithFailedSelectorByQueryToken = (queryToken: string) => {
  return createSelector(
    getDatasetResultByQueryToken(queryToken),
    getModel,
    baseRunReadyProjector({
      queryStatus: [DataViewTypes.QueryRunStatus.Succeeded, DataViewTypes.QueryRunStatus.Failed],
    })
  );
};

const baseRunReadyProjector =
  ({ queryStatus }: { queryStatus: DataViewTypes.QueryRunStatus[] }) =>
  (datasetResult: DataViewTypes.DatasetResult | void, model: FlamingoTypes.ModeModel) => {
    if (datasetResult && datasetResult.queryRunToken != null && model != null) {
      if (queryStatus.includes(datasetResult.queryRunState)) {
        return Model.Views.find(model, datasetResult.queryToken) != null;
      } else {
        return false;
      }
    } else {
      return undefined;
    }
  };
